import type { ChatType } from '@form-factory/schema';
import { Trans } from '@lingui/react/macro';
import { Link, useMatches, useNavigate } from '@tanstack/react-router';
import { Dropdown } from '@utima/ui';
import { ChevronsUpDown, PlusCircle } from 'lucide-react';
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { trpc } from '@/services/trpc';
import { useBoundStore } from '@/store/store';

export const ChatsSwitch = memo(() => {
  const matches = useMatches();
  const navigate = useNavigate();
  const { data: chats } = trpc.chats.findAll.useQuery();

  const { setChat, chat: currentChat } = useBoundStore(
    useShallow(state => ({
      setChat: state.setChat,
      chat: state.chat,
    })),
  );

  const handleChatChange = (chat: ChatType) => {
    setChat(chat);
    const lastMatch = matches.at(-1);

    if (!lastMatch) {
      return;
    }

    /**
     * When user is currently in a chat-specific route, we need to
     * redirect to the same route with the new chatId.
     */
    if ('chatId' in lastMatch.params) {
      /**
       * We need to parse the path, and always return user max up to 1
       * level deeper after `/chatId`, since we can get stuck in details of
       * components that are not related to selected chat.
       */
      const path = lastMatch.fullPath.split('/');
      const chatIdIndex = path.indexOf('$chatId');
      const newPath = path.slice(0, chatIdIndex + 2).join('/');

      return navigate({
        to: newPath,
        params: {
          chatId: chat.id,
        },
      });
    }
  };

  const hasChats = Array.isArray(chats?.items) && chats?.items?.length > 0;

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger className='m-1 flex w-full items-center justify-between rounded-radius border border-border px-3 py-2.5 text-sm font-medium transition-all hover:bg-accent'>
        {currentChat?.name ?? <Trans>Choose agent</Trans>}
        <ChevronsUpDown className='size-4' />
      </Dropdown.Trigger>
      <Dropdown.Content side='bottom' align='end' className='min-w-52'>
        {hasChats && (
          <>
            <Dropdown.Label>
              <Trans>Agents</Trans>
            </Dropdown.Label>
            <Dropdown.Group>
              {chats?.items?.map(chat => (
                <Dropdown.Item
                  className='flex items-center justify-between'
                  key={chat.id}
                  onClick={() => handleChatChange(chat as any as ChatType)}
                >
                  {chat.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Group>
            <Dropdown.Separator />
          </>
        )}
        <Dropdown.Label>
          <Trans>Management</Trans>
        </Dropdown.Label>
        <Dropdown.Group>
          <Dropdown.Item asChild>
            <Link data-cta='admin-chats-switch-add-new-link' to='/chats/create'>
              <PlusCircle className='mr-2 size-4' />
              <Trans>Add new</Trans>
            </Link>
          </Dropdown.Item>
        </Dropdown.Group>
      </Dropdown.Content>
    </Dropdown.Root>
  );
});
